import React, { useEffect, useState, useContext } from "react"
import axios from "axios"
import { FirebaseContext } from "../../firebase"
import _ from "lodash"
import PropTypes from "prop-types"

// PR
import { InputText } from "primereact/inputtext"
import { Button } from "primereact/button"

// Helpers
import { getSessStorage } from "../../helpers/sessionStorage"

// Hooks
import useLogs from "../../hooks/useLogs" // Should be rm?

// Loaders
import { LoaderProgressBar } from "../../components/Loaders"

// assets
import logo from "../../assets/images/logo-white-text.png"
import styles from "../../styles/Login.module.css"

import {
  RecaptchaVerifier,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  getMultiFactorResolver,
  TotpMultiFactorGenerator,
} from "firebase/auth"
import { callCloudFunctionNoAuth } from "../../helpers/cloudFunctionRequests"

export default function Login(props) {
  const firebase = useContext(FirebaseContext)
  const [maintenance, setMaintenance] = useState(false)

  const [loading, setLoading] = useState(false)
  const [forgotPasswordScreen, setForgotPasswordScreen] = useState(false)

  const [failedAttempts, setFailedAttempts] = useState(0)

  const [logsEnable, writeLog] = useLogs() // Should be rm?

  const [user, setUser] = useState("")
  const [password, setPassword] = useState("")

  const [err, setErr] = useState(null)
  const [msg, setMsg] = useState(null)
  const [disableBtn, setDisableBtn] = useState(false)

  // 2FA
  const [has2FA, setHas2FA] = useState(false)
  const [resolver, setResolver] = useState(null)
  const [twoFAMethod, setTwoFAMethod] = useState("")

  // 2FA: SMS
  const [verificationId, setVerificationId] = useState(null)
  const [verificationSMSCode, setVerificationSMSCode] = useState("")
  const [isResending, setIsResending] = useState(false)

  // 2FA: TOTP
  const [verificationTOTPCode, setVerificationTOTPCode] = useState("")

  let actionCodeSettings = {
    // After password reset, the user will be give the ability to go back
    // to this page.
    url: window.location.origin,
    handleCodeInApp: false,
  }

  // Recaptcha Init
  useEffect(() => {
    const recaptchaContainer = document.createElement("div")
    recaptchaContainer.id = "recaptcha-container"
    document.body.appendChild(recaptchaContainer)
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(
        "recaptcha-container",
        {
          size: "invisible",
        },
        firebase.auth
      )
      try {
        window.recaptchaVerifier.verify()
      } catch (error) {}
    }
    // Cleanup function for when the component unmounts
    return () => {
      const container = document.getElementById("recaptcha-container")
      if (container) {
        container.remove()
      }
    }
  }, []) // Empty dependency array to run only once on mount/unmount

  // Check if user has too many failed attempts
  useEffect(() => {
    if (failedAttempts >= 3) {
      setErr("Too many failed attempts. Please reset your password.")
      setForgotPasswordScreen(true)
    }
  }, [failedAttempts])

  // Check for Session Errors
  useEffect(() => {
    if (props.errSession !== "") {
      setErr(props.errSession)
      setDisableBtn(true)
      resetErrs(10000)
      setTimeout(() => {
        firebase.signOut()
      }, 4000)
    }
  }, [props.errSession])

  // 2FA Init
  useEffect(() => {
    if (twoFAMethod === "sms") {
      getVerificationId()
    }
  }, [twoFAMethod])

  const resetErrs = (timeout = 0) => {
    if (timeout) {
      setTimeout(() => {
        setErr(null)
        setMsg(null)
        props.setErrSession("")
        setDisableBtn(false)
      }, timeout)
    } else {
      setErr(null)
      setMsg(null)
      props.setErrSession("")
      setDisableBtn(false)
    }
  }

  async function getVerificationId() {
    const phoneAuthProvider = new PhoneAuthProvider(firebase.auth)
    const getMultiFactorHint = resolver.hints.find(
      ({ factorId }) => factorId === "phone"
    )
    const phoneInfoOptions = {
      multiFactorHint: getMultiFactorHint,
      session: resolver.session,
    }
    try {
      setIsResending(true)
      // Send SMS verification code
      const verificationId = await phoneAuthProvider.verifyPhoneNumber(
        phoneInfoOptions,
        window.recaptchaVerifier
      )
      setVerificationId(verificationId)
    } catch (error) {
      console.error("Error during sending SMS verification code:", error)
    } finally {
      setIsResending(false)
    }
  }

  const displayErrors = () => {
    const noDisplay = err === null && msg === null
    if (noDisplay) return null
    const logoutMsg = getSessStorage("logout") // Check this implementation?
    return (
      <div className={styles["msg-container"]}>
        {err && <div className={styles["msg-err"]}>{err}</div>}
        {msg && <div className={styles["msg-info"]}>{msg}</div>}
        {logoutMsg && <div className={styles["msg-err"]}>{logoutMsg}</div>}
      </div>
    )
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      await firebase.auth.signInWithEmailAndPassword(user, password)
    } catch (error) {
      console.log("+++++++++AUTH Error+++++++++: ", error.code, error)

      const regex = /Firebase:\s(.*?)\s\(auth\/[^)]+\)/

      if (error.code === "auth/user-not-found") {
        const match = error.message.match(regex)
        const extractedMessage = match ? match[1] : "User not found"
        setErr(extractedMessage)
        setDisableBtn(true)
        resetErrs(7000)
        return
      }

      if (error.code === "auth/wrong-password") {
        const match = error.message.match(regex)
        const extractedMessage = match ? match[1] : "Invalid password"
        setErr(extractedMessage)
        setFailedAttempts((prevAttempts) => prevAttempts + 1)
        setDisableBtn(true)
        resetErrs(7000)
        return
      }

      if (error.code === "auth/multi-factor-auth-required") {
        setHas2FA(true)

        // Set 'resolver' instance
        const resolverInstance = getMultiFactorResolver(firebase.auth, error)
        setResolver(resolverInstance)

        console.log("Resolver Instance == ", resolverInstance)

        let preferredMethod = null

        if (resolverInstance.hints.length === 2) {
          try {
            const response = await callCloudFunctionNoAuth("checkMfaSettings", { email: user })

            preferredMethod = response.data.preferred_method
          } catch (err) {
            // If error, default to last method
            preferredMethod = resolverInstance.hints[1].factorId
            console.error("Error fetching preferred method:", err)
          }
        }

        console.log("Preferred Method == ", preferredMethod)

        // Extract factorId for convenience
        const factorId = resolverInstance.hints[0].factorId

        // Set two-factor authentication method
        if (preferredMethod) {
          if (preferredMethod === "phone") {
            preferredMethod = "sms"
          }
          console.log(`2FA method: ${preferredMethod}`)
          return setTwoFAMethod(preferredMethod)
        } else if (factorId === "phone") {
          console.log("2FA method: SMS")
          return setTwoFAMethod("sms")
        } else if (factorId === "totp") {
          console.log("2FA method: TOTP")
          return setTwoFAMethod("totp")
        }
      }

      if (error.code === "auth/internal-error") {
        console.log("Internal Error**", error)
      }
    }
  }

  const handleSubmitRecover = async (e) => {
    setLoading(true)
    e.preventDefault()
    try {
      // Verificar si el usuario requiere MFA
      if (has2FA && resolver) {
        setMsg(
          "Please complete multi-factor authentication first (SMS or TOTP)."
        )
        setLoading(false)
        return // Detener hasta que MFA esté completada
      }

      // Enviar el correo de restablecimiento de contraseña
      await firebase.sendPasswordResetEmail(user, actionCodeSettings)
      if (logsEnable) {
        await writeLog({
          type: "info",
          component: "Auth",
          message: `User resets password: ${user}`,
          stack: "",
        })
      }

      const msgTxt = `If your email is registered, a password reset link has been sent. Check your inbox and spam folder.`
      setTimeout(() => setMsg(msgTxt), 2000)
      setTimeout(() => {
        setLoading(false)
        setForgotPasswordScreen(true)
        setMsg(null)
      }, 10000)
    } catch (error) {
      setLoading(false)
      setErr(error.message)
      setMsg(error.message)
    }
  }

  const loginScreen = () => {
    if (forgotPasswordScreen) return null
    if (props.maintenance.maintenance_mode) {
      return (
        <div style={{ textAlign: "center" }}>
          <h4>{props.maintenance.title}</h4>
          <p style={{ margin: "15px 0" }}>{props.maintenance.message}</p>
          <p>{props.maintenance.updated_at}</p>
        </div>
      )
    }
    return (
      <>
        <h2>Login</h2>
        <form onSubmit={handleSubmit} className="form-section">
          <div className="form-field">
            <label htmlFor="email">Email</label>
            <InputText
              id="email"
              type="email"
              autocomplete="username"
              onChange={({ target: { value } }) => setUser(_.trim(value))}
              disabled={maintenance}
            />
          </div>
          <div className="form-field">
            <label htmlFor="pass">Password</label>
            <InputText
              id="pass"
              type="password"
              onChange={({ target: { value } }) => setPassword(value)}
              disabled={maintenance}
            />
          </div>
          <Button
            label="Login"
            type="submit"
            className="btn"
            disabled={disableBtn || maintenance}
            style={{ color: "white" }}
          />
        </form>

        <button
          className={styles["plain-text-link"]}
          onClick={() => setForgotPasswordScreen(true)}
          type="button"
        >
          Forgot your password?
        </button>
      </>
    )
  }

  const resetPasswordScreen = () => {
    if (!forgotPasswordScreen) return null
    return (
      <>
        <h2>Reset your password</h2>
        <p>Enter your email to receive a link to reset your password</p>
        <form onSubmit={handleSubmitRecover} className="form-section">
          <div className="form-field">
            <label htmlFor="email">Email</label>
            <InputText
              id="email"
              onChange={({ target: { value } }) => setUser(_.trim(value))}
              disabled={loading}
            />
          </div>
          <Button
            label="Send Link"
            type="submit"
            className="btn"
            disabled={loading || _.isEmpty(user)}
          />
          {loading && (
            <div style={{ margin: "15px 0" }}>
              <LoaderProgressBar />
            </div>
          )}
        </form>
        <button
          className={styles["plain-text-link"]}
          onClick={() => setForgotPasswordScreen(false)}
          type="button"
        >
          Back to Login
        </button>
      </>
    )
  }

  const resolveSMS = async () => {
    try {
      // Ask user for the SMS verification code. Then:
      const cred = PhoneAuthProvider.credential(
        verificationId,
        verificationSMSCode
      )
      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred)

      // Complete sign-in
      const userCredential = await resolver.resolveSignIn(multiFactorAssertion)

      console.log(
        "User successfully signed in with the second factor phone number.",
        userCredential
      )

      // User successfully signed in with the second factor phone number.
    } catch (error) {
      console.error("Error during multi-factor authentication:", error)
    }
  }

  const screenForSMS = () => {
    if (twoFAMethod !== "sms") return
    const phoneLastDigits = () => {
      const phoneReolver = resolver.hints.find(
        ({ factorId }) => factorId === "phone"
      )
      return phoneReolver.phoneNumber.slice(-4)
    }
    return (
      <div className="form-section">
        <hr />
        <div className="form-field">
          <label>Verification Code (sms)</label>
          <InputText
            id="smsCode"
            name="smsCode"
            value={verificationSMSCode}
            onChange={({ target: { value } }) => setVerificationSMSCode(value)}
          />
          <small>Sent to the phone number ending in: {phoneLastDigits()}</small>
        </div>
        <Button
          type="button"
          className="btn"
          style={{ marginTop: "10px", color: "white" }}
          label="Enter Verification Code"
          onClick={resolveSMS}
          disabled={!verificationSMSCode || isResending}
        />
        <p style={{ marginTop: "5px" }}>
          Didn't receive a code?{" "}
          <button
            type="button"
            onClick={getVerificationId}
            disabled={isResending}
            style={{
              color: "blue",
              background: "none",
              border: "none",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            {isResending ? "Resending..." : "Resend Code"}
          </button>
        </p>
      </div>
    )
  }

  const resolveTOTP = async () => {
    if (!verificationTOTPCode) return

    const handleResolverUid = resolver.hints.find(
      ({ factorId }) => factorId === "totp"
    ).uid

    console.log("Resolver UID == ", handleResolverUid)

    const multiFactorAssertion = TotpMultiFactorGenerator.assertionForSignIn(
      handleResolverUid,
      verificationTOTPCode
    )
    try {
      const userCredential = await resolver.resolveSignIn(multiFactorAssertion)
      console.log(
        "Successfully signed in with the second factor TOTP",
        userCredential
      )
      // Successfully signed in!
    } catch (error) {
      console.error("Error during TOTP multi-factor sign-in:", error)
      // Invalid or expired OTP.
    }
  }

  const screenForTOTP = () => {
    if (twoFAMethod !== "totp") return
    return (
      <div className="form-section">
        <hr />
        <div className="form-field">
          <label>Verification Code</label>
          <InputText
            id="totpCode"
            name="totpCode"
            value={verificationTOTPCode}
            onChange={({ target: { value } }) => setVerificationTOTPCode(value)}
          />
        </div>
        <Button
          type="button"
          className="btn"
          style={{ marginTop: "10px" }}
          label="Enter Verification Code"
          onClick={resolveTOTP}
          disabled={!verificationTOTPCode || isResending}
        />
      </div>
    )
  }

  return (
    <div className="layout-login">
      <img src={logo} alt="Recovery Link Logo" className="main-logo" />
      <div className="login-container">
        {loginScreen()}
        {resetPasswordScreen()}
        {displayErrors()}

        {screenForSMS()}
        {screenForTOTP()}
      </div>
    </div>
  )
}

Login.propTypes = {
  setErrSession: PropTypes.func.isRequired,
  errSession: PropTypes.string,
  maintenance: PropTypes.object.isRequired,
}
